import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCircleCheck,
  faChevronRight,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { useAxiosInstance } from "../services/Interceptor";
import AthleteSearch from "../pages/common/athletes/AthleteSearch";
import LoadingSpinner from "./LoadingSpinner";
import {
  SearchAthletesPagedAsync,
  getAthleteCompareData,
  getAthleteProfile,
} from "../services/AthleteService";
import { useResponsiveHeight } from "../hooks/useResponsiveHeight.js";
import { useDebouncedValue } from "../hooks/useDebouncedValue.js";
import { ShareComparisonButton } from "./ShareComparisonButton.jsx";
import AthleteScore from "./athlete-scores/athlete-score";
import Modal from "./Modal";
import AthleteCombineHistoryModal from "./athlete-compare/AthleteCombineHistoryModal.jsx";
import CompareModalTrigger from "./athlete-compare/CompareModalTrigger.jsx";

const PAGE_SIZE = 20;

export default function ComparePlayersModal() {
  const [openCompareModal, setOpenCompareModal] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [currentAthlete, setCurrentAthlete] = useState(null);
  const apiInstance = useAxiosInstance();
  const [isAthleteSelected, setIsAthleteSelected] = useState(false);
  const searchAthleteRef = useRef();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedQ = useDebouncedValue(searchTerm, 250);
  const r_athlete = useSelector((state) => state.athletes);
  // eslint-disable-next-line no-unused-vars
  const [userId, setUserId] = useState(0);
  const [athleteCompareData, setAthleteCompareData] = useState();
  const comparisonRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [athleteId, setAthleteId] = useState(0);
  const [openCombineHistoryModal, setOpenCombineHistoryModal] = useState(false);

  const { adjustedHeight } = useResponsiveHeight({
    mobileOffset: 420,
    tabletOffset: 390,
    desktopOffset: 380,
  });

  const [showSimilarDropdown, setShowSimilarDropdown] = useState(false);
  // similarFilters drives the actual fetch; similarFiltersDraft drives the
  // checkbox UI inside the dropdown. The draft only commits to similarFilters
  // when the user clicks Apply — preventing every checkbox toggle from
  // re-running the search.
  const [similarFilters, setSimilarFilters] = useState({
    size: false,
    position: false,
    athleticism: false,
  });
  const [similarFiltersDraft, setSimilarFiltersDraft] = useState({
    size: false,
    position: false,
    athleticism: false,
  });

  // Paged results state — replaces the old Redux/cached-list flow.
  const [displayedAthletes, setDisplayedAthletes] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const cacheRef = useRef(new Map());
  const observerTarget = useRef(null);

  const toggleSimilarFilter = (key) => {
    setSimilarFiltersDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openSimilarDropdown = () => {
    setSimilarFiltersDraft(similarFilters);
    setShowSimilarDropdown(true);
  };

  const applySimilarFilters = () => {
    setSimilarFilters(similarFiltersDraft);
    setShowSimilarDropdown(false);
  };

  const buildSimilarParam = useCallback(
    () =>
      Object.entries(similarFilters)
        .filter(([, checked]) => checked)
        .map(([k]) => k)
        .join(","),
    [similarFilters],
  );

  const refAthleteId = r_athlete?.profile?.id;

  const fetchPage = useCallback(
    async (q, page, append) => {
      const similar = buildSimilarParam();
      const key = `${q}|${similar}|${refAthleteId || ""}|${page}`;
      const cached = cacheRef.current.get(key);
      if (cached) {
        if (append) {
          setDisplayedAthletes((prev) => [...prev, ...(cached.items || [])]);
        } else {
          setDisplayedAthletes(cached.items || []);
        }
        setHasMore(!!cached.hasMore);
        setCurrentPage(page);
        return;
      }

      const setBusy = append ? setIsLoadingMore : setLoading;
      setBusy(true);
      try {
        const data = await SearchAthletesPagedAsync(apiInstance, {
          q,
          page,
          pageSize: PAGE_SIZE,
          similar: similar || undefined,
          athleteId: refAthleteId,
        });
        cacheRef.current.set(key, data);
        if (append) {
          setDisplayedAthletes((prev) => [...prev, ...(data.items || [])]);
        } else {
          setDisplayedAthletes(data.items || []);
        }
        setHasMore(!!data.hasMore);
        setCurrentPage(page);
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to search athletes",
          { position: "top-right" },
        );
        if (!append) {
          setDisplayedAthletes([]);
          setHasMore(false);
        }
      } finally {
        setBusy(false);
      }
    },
    // apiInstance is stable: useAxiosInstance() memoizes the axios instance with
    // an empty deps array, so the reference doesn't change between renders.
    [apiInstance, buildSimilarParam, refAthleteId],
  );

  // Drop cached page entries whenever the similarity filter set changes. Without this,
  // entries from previously-applied filter combos accumulate for the lifetime of the
  // component (not a correctness bug — cache key includes the similar param — but
  // unbounded memory growth in a long session).
  useEffect(() => {
    cacheRef.current.clear();
  }, [similarFilters]);

  useEffect(() => {
    if (!openCompareModal || !refAthleteId) return;
    fetchPage(debouncedQ, 0, false);
  }, [openCompareModal, debouncedQ, similarFilters, refAthleteId, fetchPage]);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !loading) {
          fetchPage(debouncedQ, currentPage + 1, true);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => observer.unobserve(target);
  }, [hasMore, isLoadingMore, loading, currentPage, debouncedQ, fetchPage]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const athleteSelected = (selectedAth) => {
    setUserId(selectedAth.userId);
    setSelectedAthlete(selectedAth);
    setIsAthleteSelected(true);
    fetchData(selectedAth.userId);
  };

  const fetchData = async (uid) => {
    if (uid !== 0) {
      try {
        const response = await getAthleteProfile(apiInstance, uid);
        setSelectedAthlete(response);

        const comparedResponse = await getAthleteCompareData(
          apiInstance,
          r_athlete.profile.id,
          uid,
        );
        if (comparedResponse) {
          setAthleteCompareData(comparedResponse);
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load comparison data",
          { position: "top-right" },
        );
        // Reset the selection so the user returns to search instead of being
        // stuck on the infinite loading spinner (isAthleteSelected was already
        // set true before this call).
        handleBackToSearch();
      }
    }
  };

  useEffect(() => {
    async function loadCurrent() {
      if (r_athlete.profile.id) {
        const currentAthleteRes = await getAthleteProfile(
          apiInstance,
          r_athlete.profile.id,
        );
        if (currentAthleteRes) {
          setCurrentAthlete(currentAthleteRes);
        }
      }
    }
    loadCurrent();
  }, [apiInstance, r_athlete]);

  // Only Position Measurement Rows
  const getMeasurementRowsData = () => {
    if (!athleteCompareData?.measurements?.measurementRows) return [];

    return athleteCompareData.measurements.measurementRows.map((row) => [
      row.label,
      row.athlete.data,
      row.compareTo.data,
      row.athlete.isBest ? "1" : row.compareTo.isBest ? "2" : "0",
      row.athlete.isBest && row.compareTo.isBest ? "both" : "single",
    ]);
  };

  // Only Position Best Results
  const getBestResultsData = () => {
    if (!athleteCompareData?.bestResults) return [];

    return athleteCompareData.bestResults.map((test) => {
      const athleteResult = test.athlete?.result;
      const compareToResult = test.compareTo?.result;

      let athleteValue = "-";
      let compareToValue = "-";
      let physicalTestName = "";

      if (athleteResult) {
        if (test.physicalTestName?.toLowerCase().includes("bench press")) {
          athleteValue = `${athleteResult.stageValue} × ${athleteResult.reps}`;
          physicalTestName = test.physicalTestName;
        } else if (
          test.physicalTestName?.toLowerCase().includes("40 yard dash")
        ) {
          physicalTestName =
            test.stage === "40" ? "40 Yard Dash" : `${test.stage} Yard Split`;
          athleteValue = athleteResult.stageValue?.toFixed(2) || "-";
        } else if (
          test.physicalTestName === "Vertical jump" ||
          test.physicalTestName === "Broad jump"
        ) {
          athleteValue = athleteResult.stageValue?.toFixed(2) || "-";
          physicalTestName = test.physicalTestName;
        } else {
          athleteValue = athleteResult.stageValue?.toFixed(2) || "-";
          physicalTestName = test.physicalTestName;
        }
      }

      if (compareToResult) {
        if (test.physicalTestName?.toLowerCase().includes("bench press")) {
          compareToValue = `${compareToResult.stageValue} × ${compareToResult.reps}`;
        } else if (
          test.physicalTestName?.toLowerCase().includes("40 yard dash")
        ) {
          compareToValue = compareToResult.stageValue?.toFixed(2) || "-";
        } else if (
          test.physicalTestName === "Vertical jump" ||
          test.physicalTestName === "Broad jump"
        ) {
          compareToValue = compareToResult.stageValue?.toFixed(2) || "-";
        } else {
          compareToValue = compareToResult.stageValue?.toFixed(2) || "-";
        }
      }

      return [
        physicalTestName,
        athleteValue,
        compareToValue,
        test.athlete?.isBest ? "1" : test.compareTo?.isBest ? "2" : "0",
        test.athlete?.isBest && test.compareTo?.isBest ? "both" : "single",
      ];
    });
  };

  // Only POSITION measurement score
  const getMainMeasurementScore = () => {
    if (!athleteCompareData?.measurements?.measurementScoreRows) return null;

    const mainScoreRow =
      athleteCompareData.measurements.measurementScoreRows.find(
        (row) => row.label === "PositionMeasurementScore",
      );

    if (mainScoreRow) {
      return [
        "Position Measurement Score",
        Math.round(mainScoreRow?.athlete?.data),
        Math.round(mainScoreRow?.compareTo?.data),
        mainScoreRow.athlete.isBest
          ? "1"
          : mainScoreRow.compareTo.isBest
            ? "2"
            : "0",
        mainScoreRow.athlete.isBest && mainScoreRow.compareTo.isBest
          ? "both"
          : "single",
      ];
    }

    return null;
  };

  // Only POSITION athletic score
  const getMainAthleticScore = () => {
    const athleteScore =
      currentAthlete?.overallPositionAthleticScore?.toFixed(2);
    const compareToScore =
      selectedAthlete?.overallPositionAthleticScore?.toFixed(2);

    const isAthleteBest =
      parseFloat(athleteScore || 0) > parseFloat(compareToScore || 0);
    const isCompareToBest =
      parseFloat(compareToScore || 0) > parseFloat(athleteScore || 0);
    const isBothBest =
      parseFloat(athleteScore || 0) === parseFloat(compareToScore || 0);

    return [
      "Position Athletic Score",
      athleteScore || "0",
      compareToScore || "0",
      isAthleteBest ? "1" : isCompareToBest ? "2" : "0",
      isBothBest ? "both" : "single",
    ];
  };

  const handleViewClick = (id) => {
    setAthleteId(id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setAthleteId(0);
    setIsModalOpen(false);
  };

  const handleBackToSearch = () => {
    setSelectedAthlete(null);
    setAthleteCompareData(null);
    setUserId(0);
    setIsAthleteSelected(false);
  };

  if (r_athlete.loading)
    return (
      <div className="my-10 lg:w-full xl:h-full">
        <LoadingSpinner />
      </div>
    );

  return (
    <div className="flex items-center gap-2">
      <CompareModalTrigger
        onOpen={() => {
          setOpenCompareModal(true);
          setSearchTerm("");
          setSelectedAthlete(null);
          setAthleteCompareData(null);
          setUserId(0);
          setIsAthleteSelected(false);
          setShowSimilarDropdown(false);
          setSimilarFilters({
            size: false,
            position: false,
            athleticism: false,
          });
          setSimilarFiltersDraft({
            size: false,
            position: false,
            athleticism: false,
          });
          cacheRef.current.clear();
          setDisplayedAthletes([]);
          setCurrentPage(0);
        }}
        onCompareCombineHistory={() => setOpenCombineHistoryModal(true)}
      />

      <AthleteCombineHistoryModal
        open={openCombineHistoryModal}
        onClose={() => setOpenCombineHistoryModal(false)}
        athleteId={r_athlete.profile?.id}
        currentAthlete={currentAthlete}
      />

      {openCompareModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white w-[95%] sm:w-[90%] md:w-full max-w-3xl rounded-md relative max-h-[90vh] flex flex-col">
              <button
                onClick={() => {
                  setOpenCompareModal(false);
                  setIsAthleteSelected(false);
                  setAthleteCompareData(null);
                }}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-base z-10"
              >
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>

              {!isAthleteSelected && (
                <div name="search-athlete">
                  <div className="p-4 rounded-lg max-h-[80vh]">
                    <div className="flex items-center justify-between mb-3 pr-10">
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                        Compare to another player
                      </h2>

                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              if (showSimilarDropdown) {
                                setShowSimilarDropdown(false);
                              } else {
                                openSimilarDropdown();
                              }
                            }}
                            className="text-sm flex items-center gap-2 border border-gray-300 bg-gray-200 rounded-md px-3 py-2"
                          >
                            <svg
                              width="16px"
                              height="16px"
                              viewBox="0 0 16 16"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="#000000"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M15 2v1.67l-5 4.759V14H6V8.429l-5-4.76V2h14zM7 8v5h2V8l5-4.76V3H2v.24L7 8z"
                              />
                            </svg>
                            <span className="font-normal">
                              Similar athletes
                            </span>
                          </button>

                          {showSimilarDropdown && (
                            <div className="absolute right-0 mt-2 z-50 w-48 bg-white border rounded-md shadow-lg">
                              <div className="p-4 divide-y divide-solid">
                                <label className="flex items-center gap-2 text-sm py-2">
                                  <input
                                    type="checkbox"
                                    checked={similarFiltersDraft.size}
                                    onChange={() => toggleSimilarFilter("size")}
                                  />
                                  Size
                                </label>

                                <label className="flex items-center gap-2 text-sm py-2">
                                  <input
                                    type="checkbox"
                                    checked={similarFiltersDraft.athleticism}
                                    onChange={() =>
                                      toggleSimilarFilter("athleticism")
                                    }
                                  />
                                  Athleticism
                                </label>

                                <label className="flex items-center gap-2 text-sm py-2">
                                  <input
                                    type="checkbox"
                                    checked={similarFiltersDraft.position}
                                    onChange={() =>
                                      toggleSimilarFilter("position")
                                    }
                                  />
                                  Position
                                </label>

                                <button
                                  type="button"
                                  className="mt-2 w-full bg-red-500 hover:bg-red-600 text-white text-sm py-2 rounded-md"
                                  onClick={applySimilarFilters}
                                >
                                  Apply
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <AthleteSearch
                      handleSearch={handleSearch}
                      reference={searchAthleteRef}
                    />

                    {loading ? (
                      <div className="mt-5">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <ul
                        className="list-none p-0 mt-4 overflow-y-auto pr-3 -mr-4"
                        style={{ height: `${adjustedHeight}px` }}
                      >
                        {displayedAthletes.length > 0 ? (
                          <>
                            {displayedAthletes.map((athlete) => (
                              <li
                                key={athlete.userAthleteId || athlete.userId}
                                className="cursor-pointer p-2 hover:bg-gray-200 border mb-4 rounded-md"
                                onClick={() => athleteSelected(athlete)}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex flex-row flex-1">
                                    <img
                                      alt={`${athlete.name} ${athlete.lastname}`}
                                      src={
                                        athlete.profilePicture ||
                                        "/default-profile-img.png"
                                      }
                                      className="rounded-md h-[40px] w-[40px]"
                                    />
                                    <div className="ml-4">
                                      <p className="text-base lg:text-lg uppercase">
                                        {athlete.name} {athlete.lastname}
                                      </p>
                                      <p className="text-sm text-gray-700">
                                        {athlete.position}
                                      </p>
                                    </div>
                                  </div>
                                  <FontAwesomeIcon icon={faChevronRight} />
                                </div>
                              </li>
                            ))}

                            <div ref={observerTarget} style={{ height: "1px" }} />

                            {isLoadingMore && (
                              <li className="flex justify-center py-3">
                                <div className="text-gray-500 text-sm">
                                  Loading more athletes...
                                </div>
                              </li>
                            )}

                            {!hasMore && displayedAthletes.length > 0 && (
                              <li className="flex justify-center py-3">
                                <div className="text-gray-400 text-xs">
                                  All matches loaded ({displayedAthletes.length})
                                </div>
                              </li>
                            )}
                          </>
                        ) : (
                          <div className="w-full p-2 mt-5 flex flex-col items-center">
                            <img
                              alt="No results found"
                              src="/nodata.gif"
                              className="object-cover h-20 w-20 rounded-full border-gray-300 border"
                            />
                            <span className="mt-4 text-center text-base font-medium text-gray-600">
                              No Results Found
                            </span>
                            <p className="text-gray-500 text-sm">
                              Try adjusting the filters to find what you're
                              looking for.
                            </p>
                          </div>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {isAthleteSelected && athleteCompareData && (
                <div name="compare-athlete">
                  <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b px-4 py-3">
                    <button
                      type="button"
                      onClick={handleBackToSearch}
                      className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-gray-50 hover:text-blue-800"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                      Back
                    </button>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                      Compare Players
                    </h2>
                    <div className="flex justify-center mr-10">
                      <ShareComparisonButton comparisonRef={comparisonRef} />
                    </div>
                  </div>

                  <div
                    ref={comparisonRef}
                    className="px-3 py-3 flex-1 h-[700px] overflow-y-auto"
                    style={{ maxHeight: "80vh" }}
                  >
                    <div className="grid grid-cols-3 gap-0 min-w-[700px] text-sm">
                      <div></div>

                      <div className="text-center pb-2">
                        <div className="w-14 h-14 rounded-full mx-auto bg-gray-200">
                          <img
                            alt={athleteCompareData.athleteName}
                            src={
                              athleteCompareData.athleteProfilePic ||
                              "/default-profile-img.png"
                            }
                            className="h-full w-full rounded-full object-cover"
                          />
                        </div>
                        <h3
                          className="font-semibold mt-2 text-blue-700 text-sm cursor-pointer"
                          onClick={() =>
                            handleViewClick(athleteCompareData.athleteUserId)
                          }
                        >
                          {athleteCompareData.athleteName}
                        </h3>
                        <p className="text-gray-700 text-xs">
                          {currentAthlete?.primaryPosition}
                        </p>
                      </div>

                      <div className="font-semibold text-sm absolute left-2/3 transform -translate-x-1/2 top-28 bg-gray-600 text-white px-2 py-2 rounded-full">
                        VS
                      </div>

                      <div className="text-center pb-2">
                        <div className="w-14 h-14 rounded-full mx-auto bg-gray-200">
                          <img
                            alt={athleteCompareData.compareToName}
                            src={
                              athleteCompareData.compareToProfilePic ||
                              "/default-profile-img.png"
                            }
                            className="h-full w-full rounded-full object-cover"
                          />
                        </div>
                        <h3
                          className="font-semibold mt-2 text-blue-700 text-sm cursor-pointer"
                          onClick={() =>
                            handleViewClick(athleteCompareData.compareToUserId)
                          }
                        >
                          {athleteCompareData.compareToName}
                        </h3>
                        <p className="text-gray-700 text-xs">
                          {selectedAthlete?.primaryPosition}
                        </p>
                      </div>

                      {[
                        getMainMeasurementScore(),
                        ...getMeasurementRowsData(),
                        getMainAthleticScore(),
                        ...getBestResultsData(),
                      ]
                        .filter(Boolean)
                        .map(
                          ([
                            label,
                            left,
                            right,
                            best,
                            highlightType = "single",
                          ]) => (
                            <React.Fragment key={label}>
                              <div
                                className={`py-2 border-b text-gray-700 ${
                                  highlightType === "both"
                                    ? "bg-green-50 border-green-200"
                                    : ""
                                }`}
                              >
                                {label}
                              </div>

                              <div
                                className={`py-2 border flex justify-center items-center gap-3 ${
                                  highlightType === "both"
                                    ? "bg-green-50 border-green-200 text-green-700"
                                    : "bg-gray-50 border-gray-200 text-gray-700"
                                }`}
                              >
                                {left}
                                {best === "1" && highlightType !== "both" && (
                                  <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    className="text-green-500 text-xs ml-3"
                                  />
                                )}
                              </div>

                              <div
                                className={`py-2 border flex justify-center items-center gap-3 ${
                                  highlightType === "both"
                                    ? "bg-green-50 border-green-200 text-green-700"
                                    : "bg-gray-50 border-gray-200 text-blue-700"
                                }`}
                              >
                                {right}
                                {best === "2" && highlightType !== "both" && (
                                  <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    className="text-green-500 text-xs ml-3"
                                  />
                                )}
                              </div>
                            </React.Fragment>
                          ),
                        )}
                    </div>
                  </div>
                </div>
              )}

              {isAthleteSelected && !athleteCompareData && (
                <div className="flex justify-center items-center p-8">
                  <LoadingSpinner />
                </div>
              )}
            </div>
          </div>
          <div>
            <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
              <AthleteScore
                userId={athleteId}
                showOffers={true}
                showTrackittScore={true}
                showPositionScore={true}
                searchEvent={false}
                showProfile={true}
                showRank={true}
                showLikes={true}
                isolateProfile={true}
              />
            </Modal>
          </div>
        </>
      )}
    </div>
  );
}
